import React from 'react';

import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { Checkbox, Chip } from '@mui/material';
import { TILE_COLOURS } from './tileColours';
import { useLegendNodeDrop } from './legendDrag/useLegendNodeDrop';
import { useLegendRecordWriter } from './legendDrag/useLegendRecordWriter';
import { RECORD_KIND } from './legendDrag/legendRecordPayload';

// Two layouts, one component. On the family card the legend is a horizontal
// strip of chips, where a 15px gap between items is right. In the legend
// CONFIG modal (showCheckboxes) each entry is its own row, and that same gap
// then lands between every row — which is what made the phenotype and
// genotype lists look padded out next to the compact Diseases list. So the
// checkbox layout is a tight column instead, at the same 4px the Diseases
// list uses in that mode (LegendMenu's Container gap).
const Container = styled(Grid, {
  shouldForwardProp: (prop) => prop !== 'rows'
})(({ rows }) =>
  rows
    ? { display: 'flex', flexDirection: 'column', gap: 4, flexWrap: 'nowrap' }
    : { display: 'flex', gap: 15, flexWrap: 'wrap' }
);

const BoxHolder = styled('div')({
  display: 'flex',
  alignItems: 'center',
  marginRight: 10,

  fontSize: '1rem',
  '&[draggable="true"]': {
    cursor: 'grab'
  },
  '&[draggable="true"]:active': {
    cursor: 'grabbing'
  },
  '&.dragging': {
    opacity: 0.5
  }
});

// Phenotypes carry no colour of their own, so the drag chip wears the
// phenotype register the sidebar's record tiles already use (text 4.65:1 on
// its background).
const PHENOTYPE_CHIP = {
  background: TILE_COLOURS.phenotype.background,
  color: TILE_COLOURS.phenotype.text,
  borderColor: TILE_COLOURS.phenotype.border
};

export default function PhenotypesLegend({
  phenotypes,
  id,
  dataset,
  setDataset,
  draggable,
  labelsSet,
  setLabelsSet,
  showCheckboxes = false,
  // Drag-to-assign only (the canvas legend). See PedigreeChart.
  setPedigreeState,
  dropBlocker
}) {
  const t = useAutoTranslation();

  const phenotypeCases = React.useMemo(() => {
    const cases = {};
    (dataset || []).forEach((member) => {
      const fromPhenotypes = member.phenotypes || [];
      const fromDiagnoses = (member.diagnoses || []).filter(
        (d) => d.id?.startsWith('HP:') && d.id !== 'HP:999999'
      );
      const seen = new Set();
      [...fromPhenotypes, ...fromDiagnoses].forEach((ph) => {
        if (seen.has(ph.id)) return;
        seen.add(ph.id);
        cases[ph.id] = cases[ph.id] === undefined ? 1 : cases[ph.id] + 1;
      });
    });
    return cases;
  }, [dataset]);

  const { checkTarget, write } = useLegendRecordWriter({
    setDataset,
    setPedigreeState,
    dropBlocker,
    enabled: Boolean(draggable)
  });

  const { draggedId, rowDragProps } = useLegendNodeDrop({
    enabled: Boolean(draggable),
    memberRecords: dataset,
    chipFor: (phenotype) => ({ label: t(phenotype.name), ...PHENOTYPE_CHIP }),
    // Always a phenotype, whatever its id looks like — never inferred from the
    // prefix (see recordKindOf).
    onDrop: (phenotype, target) => {
      const kind = RECORD_KIND.phenotype;
      const member = checkTarget(phenotype, target, kind);
      if (member) write({ member, item: phenotype, kind });
    }
  });

  const handlePhenotypeToggle = (id) => {
    setLabelsSet((prev) => ({
      ...prev,
      phenotypesMap: {
        ...prev.phenotypesMap,
        [id]: !prev.phenotypesMap?.[id]
      }
    }));
  };

  return (
    <>
      <Grid>
        <Container container id={id} rows={showCheckboxes ? 1 : 0}>
          {phenotypes
            ?.filter(
              (el) =>
                showCheckboxes ||
                !labelsSet?.phenotypesMap ||
                labelsSet.phenotypesMap[el.id] !== false
            )
            .map((el, i) => {
              const isDragging = draggedId === el.id;

              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center'
                  }}
                >
                  {labelsSet && showCheckboxes && (
                    <Checkbox
                      checked={labelsSet?.phenotypesMap?.[el.id] ?? true}
                      onChange={() => handlePhenotypeToggle(el.id)}
                      size="small"
                    />
                  )}
                  <BoxHolder
                    {...rowDragProps(el)}
                    className={isDragging ? 'dragging' : ''}
                    title={
                      draggable
                        ? `Drag ${el.name} to assign to a family member`
                        : undefined
                    }
                  >
                    <span style={{ fontSize: '1rem' }}>
                      {t(el.name)}{' '}
                      <span style={{ fontSize: '0.85rem', color: '#757575' }}>
                        ({el.id})
                      </span>
                      <Chip
                        label={`${phenotypeCases[el.id] ?? 0} cases`}
                        size={'small'}
                        sx={{
                          fontStyle: 'italic',
                          fontSize: 11,
                          mx: 1,
                          height: 1
                        }}
                      />
                    </span>
                  </BoxHolder>
                </div>
              );
            })}
        </Container>
      </Grid>
    </>
  );
}
